import { useState, useRef, useEffect } from 'react';
import FilterChip from './FilterChip';
import AddFilterPicker from './AddFilterPicker';

/**
 * FilterRail — persistent chip rail + add-filter picker host.
 *
 * Renders the [+ Add filter] button followed by one FilterChip per active
 * filter, plus a "Clear all" affordance when ≥2 chips active. Opens the
 * AddFilterPicker on add-button click or on chip body click (scoped to
 * that axis).
 *
 * Props:
 *   filters       — current active filters array
 *   documents     — full doc list (for value enumeration in the picker)
 *   docsForFacets — row set for facet counting (defaults to documents)
 *   ctx           — opaque context passed to picker
 *   axes          — axis registry (defaults to inventory FILTER_AXES via FilterChip)
 *   getRows       — picker-rows fn (defaults to inventory getContextualPickerRows)
 *   addHint       — optional hint text shown after "Add filter" when no chips
 *   onAdd         — ({axis, value}) => void
 *   onRemove      — (axis, value) => void
 *   onClearAll    — () => void
 */
export default function FilterRail({
  filters = [],
  documents = [],
  docsForFacets,
  ctx,
  axes,
  getRows,
  addHint = ' — Purpose, Type, Status, …',
  onAdd,
  onRemove,
  onClearAll,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scopedAxis, setScopedAxis] = useState(null);
  const addButtonRef = useRef(null);

  function openPicker(axis = null) {
    setScopedAxis(axis);
    setPickerOpen(true);
  }

  // Cmd-K / Ctrl-K opens the picker focused. Trivial precursor to the
  // full Phase 4 command palette — that phase will rebind this key.
  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // Don't hijack when an editable element is focused (search input,
        // textarea, contenteditable). Lawyers typing in the search box
        // shouldn't have Cmd-K stolen.
        const t = e.target;
        const tag = (t?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
        e.preventDefault();
        openPicker(null);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  function handleSelect({ axis, value }) {
    // Toggle: if this exact chip is already active, remove it; otherwise add.
    const isActive = filters.some(f => f.axis === axis && f.value === value);
    if (isActive) {
      onRemove?.(axis, value);
    } else {
      onAdd?.({ axis, value });
    }
    // Stay open in scoped mode (multi-pick within an axis). Close otherwise.
    if (!scopedAxis) {
      setPickerOpen(false);
      setScopedAxis(null);
    }
  }

  function handleClose() {
    setPickerOpen(false);
    setScopedAxis(null);
  }

  const hasFilters = filters.length > 0;

  return (
    <div className="filter-rail">
      <div className="filter-rail-add-wrap">
        <button
          ref={addButtonRef}
          type="button"
          className="filter-rail-add"
          onClick={() => openPicker(null)}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
        >
          <span className="filter-rail-add-plus">+</span>
          <span className="filter-rail-add-label">
            Add filter
            {!hasFilters && (
              <span className="filter-rail-add-hint">{addHint}</span>
            )}
          </span>
        </button>
        {pickerOpen && (
          <AddFilterPicker
            documents={documents}
            docsForFacets={docsForFacets || documents}
            ctx={ctx}
            getRows={getRows}
            activeFilters={filters}
            scopedAxis={scopedAxis}
            onSelect={handleSelect}
            onClose={handleClose}
            anchorRef={addButtonRef}
          />
        )}
      </div>

      {filters.map(f => (
        <FilterChip
          key={`${f.axis}|${f.value}`}
          filter={f}
          axes={axes}
          onEdit={(axis) => openPicker(axis)}
          onRemove={onRemove}
        />
      ))}

      {filters.length >= 2 && (
        <button
          type="button"
          className="filter-rail-clear"
          onClick={onClearAll}
        >
          Clear all
        </button>
      )}
    </div>
  );
}
